// src/pages/PrivacyPolicy.tsx

import React from 'react';
import Navigation from '../components/Navigation';

const PrivacyPolicy: React.FC = () => {
  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', lineHeight: '1.6' }}>
        <h1 style={{ marginBottom: '1rem' }}>Privacy Policy</h1>
        <p style={{ color: '#666', marginBottom: '2rem', fontSize: '0.875rem' }}>
          <strong>Last Updated:</strong> January 21, 2025
        </p>

        <section style={{ marginBottom: '2rem' }}>
          <h2>1. Introduction</h2>
          <p>
            Help Keep My Money LLC ("we," "us," or "our") is committed to protecting your privacy. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
            when you use our web-based application and services (the "Service").
          </p>
          <p>
            Please read this Privacy Policy carefully. By accessing or using our Service, you agree to 
            the collection and use of information in accordance with this policy.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>2. Information We Collect</h2>
          
          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>2.1 Personal Information</h3>
          <p>We collect information that you provide directly to us, including:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, phone number, address</li>
            <li><strong>Business Information:</strong> Business name, EIN, business type, industry</li>
            <li><strong>Financial Information:</strong> Payment information, subscription details, billing information</li>
            <li><strong>Documents:</strong> Files, documents, and attachments you upload to the Service</li>
            <li><strong>Entity Formation Data:</strong> Information provided in entity formation intake forms</li>
            <li><strong>Task Information:</strong> Task assignments, comments, and related communications</li>
          </ul>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>2.2 Automatically Collected Information</h3>
          <p>When you use our Service, we automatically collect certain information, including:</p>
          <ul>
            <li>Device information (IP address, browser type, operating system)</li>
            <li>Usage data (pages visited, time spent, features used)</li>
            <li>Authentication tokens and session information</li>
            <li>Log files and error reports</li>
          </ul>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>2.3 Cookies and Tracking Technologies</h3>
          <p>
            We use cookies and similar tracking technologies to track activity on our Service and hold 
            certain information. You can instruct your browser to refuse all cookies or to indicate when 
            a cookie is being sent.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for the following purposes:</p>
          <ul>
            <li>To provide, maintain, and improve our Service</li>
            <li>To process your requests, transactions, and payments</li>
            <li>To communicate with you about your account, tasks, and our services</li>
            <li>To send you important notices and updates</li>
            <li>To comply with legal obligations and regulatory requirements</li>
            <li>To detect, prevent, and address technical issues and security threats</li>
            <li>To personalize your experience and provide customer support</li>
            <li>To facilitate entity formation services and document management</li>
          </ul>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>4. Information Sharing and Disclosure</h2>
          
          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>4.1 Service Providers</h3>
          <p>
            We may share your information with third-party service providers who perform services on our 
            behalf. These service providers are contractually obligated to protect your information and use it 
            solely for the purposes we specify. Below is a detailed list of our service providers:
          </p>
          <ul>
            <li><strong>Firebase (Google Cloud Platform):</strong> 
              <ul>
                <li>Purpose: Authentication, database storage (Firestore), file storage, hosting infrastructure</li>
                <li>Data Shared: User account information, documents, application data, authentication tokens</li>
                <li>Privacy Policy: <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">https://firebase.google.com/support/privacy</a></li>
              </ul>
            </li>
            <li><strong>Vercel:</strong> 
              <ul>
                <li>Purpose: Application hosting, content delivery network (CDN), serverless functions</li>
                <li>Data Shared: Application usage data, IP addresses, request logs</li>
                <li>Privacy Policy: <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">https://vercel.com/legal/privacy-policy</a></li>
              </ul>
            </li>
            <li><strong>Stripe:</strong> 
              <ul>
                <li>Purpose: Payment processing, subscription management, billing</li>
                <li>Data Shared: Payment card information (tokenized), billing addresses, transaction data</li>
                <li>Privacy Policy: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a></li>
              </ul>
            </li>
            <li><strong>PayPal:</strong> 
              <ul>
                <li>Purpose: Payment processing, alternative payment method</li>
                <li>Data Shared: Payment information, billing addresses, transaction data</li>
                <li>Privacy Policy: <a href="https://www.paypal.com/us/webapps/mpp/ua/privacy-full" target="_blank" rel="noopener noreferrer">https://www.paypal.com/us/webapps/mpp/ua/privacy-full</a></li>
              </ul>
            </li>
            <li><strong>Resend:</strong> 
              <ul>
                <li>Purpose: Email delivery services, transactional emails, notifications</li>
                <li>Data Shared: Email addresses, email content, delivery status</li>
                <li>Privacy Policy: <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">https://resend.com/legal/privacy-policy</a></li>
              </ul>
            </li>
            <li><strong>Twilio:</strong> 
              <ul>
                <li>Purpose: SMS notification services, phone number verification</li>
                <li>Data Shared: Phone numbers, SMS message content, delivery status</li>
                <li>Privacy Policy: <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer">https://www.twilio.com/legal/privacy</a></li>
              </ul>
            </li>
            <li><strong>OpenAI:</strong> 
              <ul>
                <li>Purpose: AI-powered tools and services, natural language processing</li>
                <li>Data Shared: Text input for AI processing, prompts, generated content</li>
                <li>Privacy Policy: <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">https://openai.com/policies/privacy-policy</a></li>
              </ul>
            </li>
            <li><strong>Bluehost (Hosting Provider):</strong> 
              <ul>
                <li>Purpose: File storage for uploaded documents and attachments</li>
                <li>Data Shared: Uploaded files, documents, attachments</li>
                <li>Privacy Policy: <a href="https://www.bluehost.com/terms/privacy-policy" target="_blank" rel="noopener noreferrer">https://www.bluehost.com/terms/privacy-policy</a></li>
              </ul>
            </li>
          </ul>
          <p style={{ marginTop: '1rem' }}>
            <strong>Opt-Out of Information Sharing:</strong> You may opt-out of certain information sharing 
            with service providers by contacting us at info@helpkeepmymoney.com. However, please note that 
            some sharing is necessary for us to provide our services (e.g., payment processing, email delivery).
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>4.2 Legal Requirements</h3>
          <p>We may disclose your information if required to do so by law or in response to valid requests by public authorities.</p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>4.3 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, or sale of assets, your information may be transferred 
            as part of that transaction.
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>4.4 We Do Not Sell Your Personal Information</h3>
          <p>
            We do not sell, rent, or trade your personal information to third parties for their marketing 
            purposes.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational security measures to protect your personal 
            information, including:
          </p>
          <ul>
            <li>Encryption of data in transit using SSL/TLS protocols</li>
            <li>Encryption of data at rest using AES-256 encryption</li>
            <li>Role-based access controls to limit who can access your information</li>
            <li>Regular security assessments and updates</li>
            <li>Secure authentication and authorization mechanisms</li>
            <li>Audit logging of access and modifications to your data</li>
          </ul>
          <p>
            However, no method of transmission over the Internet or electronic storage is 100% secure. 
            While we strive to use commercially acceptable means to protect your information, we cannot 
            guarantee absolute security.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to provide our services, comply 
            with legal obligations, resolve disputes, and enforce our agreements. When we no longer need 
            your information, we will securely delete or anonymize it in accordance with our data retention 
            policies and applicable law.
          </p>
          <p>
            <strong>Retention Periods:</strong>
          </p>
          <ul>
            <li>Account information: Retained while your account is active and for 7 years after account closure for legal compliance</li>
            <li>Financial records: Retained for 7 years as required by tax and accounting regulations</li>
            <li>Documents and attachments: Retained according to your instructions or until account deletion</li>
            <li>Audit logs: Retained for 6 years for security and compliance purposes</li>
          </ul>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>7. Your Privacy Rights (CCPA/CPRA)</h2>
          <p>
            If you are a California resident, you have certain rights under the California Consumer Privacy 
            Act (CCPA) and California Privacy Rights Act (CPRA):
          </p>
          
          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>7.1 Right to Know</h3>
          <p>
            You have the right to request that we disclose what personal information we collect, use, 
            disclose, and sell about you.
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>7.2 Right to Delete</h3>
          <p>
            You have the right to request that we delete your personal information, subject to certain 
            exceptions (e.g., legal obligations, business purposes).
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>7.3 Right to Correct</h3>
          <p>
            You have the right to request correction of inaccurate personal information.
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>7.4 Right to Portability</h3>
          <p>
            You have the right to request that we provide your personal information in a portable, 
            machine-readable format.
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>7.5 Right to Opt-Out</h3>
          <p>
            You have the right to opt-out of the sale or sharing of your personal information. As stated 
            above, we do not sell your personal information.
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>7.6 Right to Limit Use of Sensitive Information</h3>
          <p>
            You have the right to limit our use and disclosure of sensitive personal information to 
            specific purposes.
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>7.7 Exercising Your Rights</h3>
          <p>
            To exercise any of these rights, you can:
          </p>
          <ol style={{ marginBottom: '1rem' }}>
            <li>
              <strong>Submit a request online:</strong> Log in to your account and visit the{' '}
              <a href="/privacy-requests" style={{ color: '#007bff', textDecoration: 'none' }}>
                Consumer Privacy Rights
              </a>{' '}
              page to submit your request.
            </li>
            <li>
              <strong>Contact us directly:</strong> Email or call us at:
            </li>
          </ol>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderRadius: '4px',
            marginTop: '0.5rem',
            marginBottom: '1rem'
          }}>
            <p style={{ margin: 0 }}>
              <strong>Email:</strong> info@helpkeepmymoney.com<br />
              <strong>Phone:</strong> (661) 524-6674<br />
              <strong>Address:</strong> Help Keep My Money LLC, 1752 E. Ave. J, Suite 214, Lancaster, CA 93535
            </p>
          </div>
          <p style={{ marginTop: '0.5rem' }}>
            We will respond to your request within 45 days. We may need to verify your identity before 
            processing your request.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>8. Financial Privacy (GLBA)</h2>
          <p>
            If you are a customer who uses our financial services, you have additional privacy rights 
            under the Gramm-Leach-Bliley Act (GLBA):
          </p>
          
          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>8.1 Information We Collect</h3>
          <p>
            We collect nonpublic personal information ("NPI") about you from:
          </p>
          <ul>
            <li>Information you provide on applications and forms</li>
            <li>Information about your transactions with us</li>
            <li>Information we receive from third parties</li>
          </ul>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>8.2 How We Share Your Information</h3>
          <p>
            We do not disclose your NPI to nonaffiliated third parties except as necessary to provide 
            our services or as permitted or required by law. We may share your information with:
          </p>
          <ul>
            <li>Service providers who assist us in providing our services</li>
            <li>As required by law, regulation, or legal process</li>
            <li>To protect our rights and property</li>
          </ul>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>8.3 Opt-Out Rights</h3>
          <p>
            Federal law gives you the right to limit some, but not all, sharing of your NPI. You may 
            opt-out of certain information sharing by contacting us at the address above.
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>8.4 Safeguarding Your Information</h3>
          <p>
            We maintain physical, electronic, and procedural safeguards that comply with federal standards 
            to protect your NPI. We restrict access to your NPI to employees who need to know that 
            information to provide services to you.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>9. Children's Privacy (COPPA Compliance)</h2>
          <p>
            Our Service is not intended for individuals under the age of 18. We take special care to protect 
            the privacy of minors and comply with the Children's Online Privacy Protection Act (COPPA) and 
            California privacy laws.
          </p>
          
          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>9.1 Age Restrictions</h3>
          <p>
            <strong>Users Under 13:</strong> Our Service is not intended for children under 13 years of age. 
            We do not knowingly collect personal information from children under 13. If you are under 13, 
            please do not use our Service or provide any personal information to us.
          </p>
          <p>
            <strong>Users 13-16:</strong> If you are between 13 and 16 years of age, you must have your 
            parent or guardian's consent before using our Service. By creating an account, you represent 
            that you have obtained such consent.
          </p>
          <p>
            <strong>Users 16-17:</strong> If you are between 16 and 17 years of age, you may use our 
            Service with appropriate parental guidance. We recommend that parents or guardians review 
            this Privacy Policy with you.
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>9.2 Parental Rights</h3>
          <p>
            If you are a parent or guardian and discover that your child under 13 has provided us with 
            personal information without your consent, please contact us immediately at:
          </p>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderRadius: '4px',
            marginTop: '0.5rem',
            marginBottom: '1rem'
          }}>
            <p style={{ margin: 0 }}>
              <strong>Email:</strong> info@helpkeepmymoney.com<br />
              <strong>Phone:</strong> (661) 524-6674<br />
              <strong>Subject Line:</strong> "COPPA Request - Child Privacy"
            </p>
          </div>
          <p>
            Upon verification of your identity and your child's information, we will:
          </p>
          <ul>
            <li>Immediately delete the child's personal information from our systems</li>
            <li>Provide you with a summary of what information was collected</li>
            <li>Refund any payments made by the child, if applicable</li>
          </ul>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>9.3 California Minors (13-16)</h3>
          <p>
            Under California law, minors between 13 and 16 years of age must provide affirmative consent 
            (opt-in) before we can sell or share their personal information. We do not sell personal 
            information, but if we were to do so in the future, we would require explicit opt-in consent 
            from minors in this age range.
          </p>

          <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>9.4 Age Verification</h3>
          <p>
            During account registration, we collect your date of birth to verify that you meet our age 
            requirements. This information is used solely for age verification and compliance purposes 
            and is not shared with third parties except as required by law.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>10. International Users</h2>
          <p>
            Our Service is operated from the United States. If you are accessing our Service from outside 
            the United States, please be aware that your information may be transferred to, stored, and 
            processed in the United States. By using our Service, you consent to this transfer.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material 
            changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. 
            You are advised to review this Privacy Policy periodically for any changes.
          </p>
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <h2>12. Contact Us</h2>
          <p>If you have questions about this Privacy Policy, please contact us:</p>
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1rem',
            borderRadius: '4px',
            marginTop: '0.5rem'
          }}>
            <p style={{ margin: '0.5rem 0' }}>
              <strong>Help Keep My Money LLC</strong><br />
              Email: info@helpkeepmymoney.com<br />
              Phone: (661) 524-6674<br />
              Address: 1752 E. Ave. J, Suite 214, Lancaster, CA 93535
            </p>
          </div>
        </section>

        <section style={{
          marginTop: '3rem',
          padding: '1.5rem',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px'
        }}>
          <h3 style={{ marginTop: 0, color: '#856404' }}>Do Not Sell My Personal Information</h3>
          <p style={{ color: '#856404', margin: 0 }}>
            We do not sell your personal information. If this changes in the future, we will update this 
            Privacy Policy and provide you with an opt-out mechanism as required by law.
          </p>
        </section>

        <div style={{
          marginTop: '3rem',
          padding: '1rem',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          fontSize: '0.875rem',
          color: '#495057'
        }}>
          <p style={{ margin: 0 }}>
            <strong>Note:</strong> This Privacy Policy is a legal document. We recommend that you review 
            it with legal counsel to ensure it meets all applicable requirements for your specific use 
            case and jurisdiction.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
